import React, { useState, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { message } from "antd";
import BarcodeScannerModal from "../BarcodeScannerModal";
import ReceiptUploadModal from "../ReceiptUploadModal";
import JobInfoSection from "./JobInfoSection";
import ProductsSection from "./ProductsSection";
import PaymentsSection from "./PaymentsSection";
import ReceiptLoadingModal from "./ReceiptLoadingModal";
import * as accountSettingsService from "../../../../../services/accountSettingsService";
import { getAppliedDiscount } from "../../../../../utils/pricingTiers";

const JobDetailModal = ({
  job,
  isOpen,
  onClose,
  onUpdateJob,
  onRemoveReceipt,
  onClearAllReceipts,
  onRemoveMaterial,
  onUpdateMaterial,
  onAddMaterial,
  onAddReceipt,
  onCompleteJob,
  onRevertJob,
  products,
  productsLoading,
  productsError,
  showReceiptLoading,
  setShowReceiptLoading
}) => {
  const jobInfoRef = useRef(null);

  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Local state for live cost updates (so Payment button appears immediately)
  const [liveIncludeTax, setLiveIncludeTax] = useState(job?.includeTax || false);

  // Fetch account settings for global pricing tiers
  const { data: accountSettings } = useQuery({
    queryKey: ["accountSettings"],
    queryFn: accountSettingsService.getAccountSettings,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Callback to update live costs from JobInfoSection
  const handleLiveCostUpdate = useCallback((updates) => {
    if (updates.includeTax !== undefined) {
      setLiveIncludeTax(updates.includeTax);
    }
  }, []);

  // Extract products from sales data (do NOT aggregate - show each addition separately)
  const orderProducts = useMemo(() => {
    if (!job || !job.sales) return [];

    const allProducts = [];
    const salesArray = Array.isArray(job.sales) ? job.sales : [];

    salesArray.forEach((sale) => {
      if (sale && sale.saleItems) {
        // Ensure saleItems is an array (it might come as Set from Java)
        const itemsArray = Array.isArray(sale.saleItems) ? sale.saleItems : Object.values(sale.saleItems);

        itemsArray.forEach((saleItem) => {
          if (saleItem && saleItem.product) {
            // Use wholesale price if available, otherwise use unit price
            const price = parseFloat(saleItem.unitPrice || saleItem.product.wholesalePrice || saleItem.product.unitPrice || 0);
            const product = {
              id: saleItem.id,
              name: saleItem.product.name,
              price: price,
              quantity: saleItem.quantity || 1,
              category: saleItem.product.category || "N/A",
              notes: sale.description || "",
              saleId: sale.id,
              productId: saleItem.product.id
            };
            allProducts.push(product);
          }
        });
      }
    });
    return allProducts;
  }, [job]);

  // Calculate total quantity
  const totalQuantity = useMemo(() => {
    return orderProducts.reduce((sum, p) => sum + (parseInt(p.quantity) || 0), 0);
  }, [orderProducts]);

  // Get current discount (only applies if manually selected, not AUTO)
  const currentDiscount = useMemo(() => {
    return getAppliedDiscount(job, accountSettings, totalQuantity);
  }, [job, accountSettings, totalQuantity]);

  // Calculate product cost from added products (with manually selected discount)
  const productCost = useMemo(() => {
    if (!orderProducts || orderProducts.length === 0) return 0;
    const subtotal = orderProducts.reduce((sum, product) => {
      return sum + (product.price * product.quantity);
    }, 0);
    // Apply discount only if manually selected
    const discountMultiplier = 1 - (currentDiscount.discount / 100);
    return subtotal * discountMultiplier;
  }, [orderProducts, currentDiscount.discount]);

  // Calculate total cost (product cost + tax) for billing
  const taxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
  const totalCost = useMemo(() => {
    const tax = liveIncludeTax ? productCost * taxRate : 0;
    return productCost + tax;
  }, [productCost, liveIncludeTax, taxRate]);

  if (!isOpen || !job) return null;

  const removeProduct = async (productId) => {
    if (!onRemoveMaterial) return;

    const product = orderProducts.find(p => p.id === productId);

    if (!product) {
      console.error("Product not found in orderProducts for id:", productId);
      message.error("Product not found");
      return;
    }

    if (!product.saleId) {
      console.error("Product missing saleId:", product);
      message.error("Product information is incomplete");
      return;
    }

    try {
      await onRemoveMaterial({ jobId: job.id, materialId: product.saleId });
      message.success("Product removed successfully");
    } catch (error) {
      console.error("Failed to remove product:", error);
      // Extract error message from backend response
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "Failed to remove product. Please try again.";
      message.error({
        content: errorMessage,
        duration: 6, // Show longer for validation errors
      });
    }
  };

  // Handle modal close with auto-save of pending changes
  const handleClose = async () => {
    // Save any unsaved material cost or job price changes before closing
    if (jobInfoRef.current) {
      await jobInfoRef.current.saveAllPendingChanges();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start justify-center z-50 overflow-y-auto" onClick={handleClose}>
      <div className="bg-white w-full min-h-screen sm:min-h-0 sm:rounded-lg sm:max-w-4xl sm:my-4 sm:mx-4 md:my-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header - Sticky */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-4 py-3 flex justify-between items-center z-10">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold m-0 truncate pr-2">
            Job #{job.id} - {job.title}
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-2xl text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 pb-8">

        {/* Order Details */}
        <JobInfoSection
          ref={jobInfoRef}
          job={job}
          productCost={productCost}
          onCompleteJob={() => onCompleteJob && onCompleteJob(job.id)}
          onRevertJob={() => onRevertJob && onRevertJob(job.id)}
          onUpdateJob={onUpdateJob}
          onLiveCostUpdate={handleLiveCostUpdate}
          accountSettings={accountSettings}
        />

        {/* Payments Section */}
        <PaymentsSection job={job} totalCost={totalCost} />

        {/* Products Section */}
        <ProductsSection
          orderProducts={orderProducts}
          job={job}
          onAddProduct={onAddMaterial}
          onScanBarcode={() => setShowBarcodeScanner(true)}
          onRemoveProduct={removeProduct}
          onUpdateProduct={onUpdateMaterial}
          availableProducts={products}
          productsLoading={productsLoading}
          productsError={productsError}
          accountSettings={accountSettings}
          totalQuantity={totalQuantity}
          onUpdateJob={onUpdateJob}
        />

        {/* Barcode Scanner Modal */}
        {showBarcodeScanner && (
          <BarcodeScannerModal
            jobId={job.id}
            isOpen={showBarcodeScanner}
            onClose={() => setShowBarcodeScanner(false)}
            onProductFound={onAddMaterial}
            isMobile={/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)}
          />
        )}

        {/* Receipt Upload Modal */}
        {showReceiptModal && (
          <ReceiptUploadModal
            jobId={job.id}
            isOpen={showReceiptModal}
            isProcessing={showReceiptLoading}
            onClose={() => {
              setShowReceiptModal(false);
              setShowReceiptLoading(false);
            }}
            onAddReceipt={async (file, jobId) => {
              try {
                // Show loading state and keep modal open to show processing
                setShowReceiptLoading(true);
                
                // Handle receipt upload and show verification
                await onAddReceipt(file, jobId);
                
              } catch (error) {
                console.error("Receipt upload failed:", error);
              } finally {
                // Always close modal and reset loading state
                setShowReceiptModal(false);
                setShowReceiptLoading(false);
              }
            }}
          />
        )}

        {/* Receipt Loading Modal */}
        <ReceiptLoadingModal isOpen={showReceiptLoading} />
        </div>
      </div>
    </div>
  );
};

export default JobDetailModal;
